import { Button } from "@/components/Button/Button";
import { DialogBox } from "@/components/DialogBox/DialogBox";
import { DownloadCaseSvg, DownloadDesignSvg, ExpandedSvg, GeneratingSvg } from "@/components/Icon/Icon";
import { COMPLETE_CASE_STATUSES, NOT_DOWNLOADABLE_CASE_STATUES } from "@/constants/common";
import { CASE_TABLE_STATE_NAME } from "@/constants/designTableConstants";
import { Service, useOrgCredits, useServiceCost } from "@/hooks/credits";
import { Scope, usePermissions } from "@/hooks/permissions";
import { CaseReadPublic, clientApi, useMarkCaseAsDownloadedApiV1CasesPutMutation } from "@/store/clientApi";
import { selectCheckedIds, selectNumChecked, selectRtkData } from "@/store/slices/tableSlice";
import { fetchModel } from "@/store/slices/viewportSlice";
import { RootState, store } from "@/store/store";
import { theme } from "@/themes/theme";
import { ModelFileTypes } from "@/typed/ModelFileTypes";
import { ThumbnailFileTypes } from "@/typed/ThumbnailFileTypes";
import { getFileTypeFromS3URL } from "@/utils/getFileType";
import { ensureError, localizeError } from "@/utils/helpers";
import { getDataFromIndexedDB, Store } from "@/utils/indexeddb";
import {
  alpha,
  Box,
  ButtonGroup,
  CircularProgress,
  ClickAwayListener,
  Grow,
  LinearProgress,
  MenuItem,
  MenuList,
  Divider as MUIDivider,
  Paper,
  Popper,
  Typography
} from "@mui/material";
import { linearProgressClasses } from "@mui/material/LinearProgress";
import { createSelector } from "@reduxjs/toolkit";
import JSZip from "jszip";
import { forwardRef, Fragment, MouseEventHandler, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { shallowEqual, useSelector } from "react-redux";

const Divider = ({ backgroundColor, color }: { backgroundColor: string; color: string }) => {
  // TODO there should be a nicer way to do a divider at 80% height and centered.
  return (
    <Box sx={{ display: "flex", alignItems: "center", bgcolor: backgroundColor }}>
      <Box sx={{ bgcolor: color, height: "80%", width: "1px" }}></Box>
    </Box>
  );
};

type DownloadDesignsButtonProps = {
  selectedCase?: string[]; // This is used when not using the main download button
  dropdownPlacement?: "top" | "bottom";
};

// Helper function to generate a unique folder name in the parent zip
function getUniqueFolderName(parentZip: JSZip, baseName: string): string {
  let uniqueName = baseName;
  let counter = 1;
  // If a folder with the uniqueName already exists, append counter
  while (parentZip.files[uniqueName + "/"]) {
    uniqueName = `${baseName}-${counter}`;
    counter++;
  }
  return uniqueName;
}

const showDownloadToast = ({
  onClick,
  amount,
  total,
  id
}: {
  onClick?: MouseEventHandler<HTMLDivElement>;
  amount: number;
  total: number;
  id?: string;
}) => {
  return toast.custom(<DownloadToast onClick={onClick} amount={amount} total={total} />, {
    duration: Number.POSITIVE_INFINITY,
    id: id,
    icon: <GeneratingSvg color="inherit" />
  });
};

const DownloadToastUndoButton = forwardRef(function DownloadToastUndoButton(
  { onClick }: { onClick?: MouseEventHandler<HTMLDivElement> },
  ref
) {
  const { t } = useTranslation("cases");
  return (
    <Box
      ref={ref}
      sx={{
        width: "fit-content",
        margin: {
          xs: "-16px -8px -16px -8px",
          sm: "-16px -8px -16px -8px",
          md: "-20px -12px -20px -12px",
          lg: "-24px -16px -24px -16px"
        }
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "0px",
          height: "100%"
        }}
      >
        <MUIDivider orientation="vertical" sx={{ height: "100%", borderWidthRight: "2px", borderColor: alpha("#ffffff", 0.16) }} />
      </Box>
      <Box
        component="div"
        sx={{
          marginLeft: "4px",
          height: "100%",
          cursor: "pointer",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          "&:hover": {
            bgcolor: alpha("#ffffff", 0.04)
          }
        }}
        onClick={onClick}
      >
        <Box sx={{ marginRight: "12px", marginLeft: "8px" }}>
          <Typography>{t("common:button.undo")}</Typography>
        </Box>
      </Box>
    </Box>
  );
});

const DownloadToast = ({ onClick, amount, total }: { onClick?: MouseEventHandler<HTMLDivElement>; amount: number; total: number }) => {
  const { t } = useTranslation("cases");
  const [buttonWidth, setButtonWidth] = useState(0);
  const ref = useRef(null);
  const progress = total > 0 ? (amount / total) * 100 : 100;
  const finished = progress >= 100;

  useEffect(() => {
    if (ref.current == null || finished) {
      setButtonWidth(0);
      return;
    }
    const observer = new ResizeObserver((entries) => {
      setButtonWidth(entries[0].contentBoxSize[0].inlineSize);
    });
    observer.observe(ref.current);

    return () => {
      observer.disconnect();
    };
  }, [ref, setButtonWidth]); //eslint-disable-line

  return (
    <>
      <Box
        component="div"
        sx={{
          width: "100%",
          display: "flex"
        }}
      >
        <Typography sx={{ mr: "24px", width: "100%" }}>
          {total == 1 ? t("toast.processingUpload") : t("toast.processingUploads", { amount, total })}
        </Typography>
        <Box
          sx={{
            position: "absolute",
            bottom: "0px",
            left: "0px",
            width: `calc(100% - ${buttonWidth}px)`
          }}
        >
          <LinearProgress
            variant={finished ? "indeterminate" : "determinate"}
            value={progress}
            sx={{
              [`&.${linearProgressClasses.colorPrimary}`]: {
                backgroundColor: alpha("#ffffff", 0.16)
              },
              [`& .${linearProgressClasses.bar}`]: {
                backgroundColor: alpha("#ffffff", 0.6),
                boxShadow: theme.progress.shadows.fill
              }
            }}
          />
        </Box>
        {!finished && <DownloadToastUndoButton ref={ref} onClick={onClick} />}
      </Box>
    </>
  );
};

type DownloadType = "scan" | "based-model";
interface ModelDownloadData {
  lower?: { data: ArrayBuffer; fileType: ModelFileTypes | ThumbnailFileTypes | undefined };
  upper?: { data: ArrayBuffer; fileType: ModelFileTypes | ThumbnailFileTypes | undefined };
}

const getDataFromCache = async (caseId: string, downloadType: DownloadType): Promise<ModelDownloadData> => {
  const cachedModels = await getDataFromIndexedDB(caseId, downloadType === "scan" ? Store.ScanStore : Store.ModelOutputStore);

  const data: ModelDownloadData = {};

  // Process models from cache
  if (cachedModels && Array.isArray(cachedModels.data)) {
    const lowerCached = cachedModels.data.find((item) => item.label === "lower");
    const upperCached = cachedModels.data.find((item) => item.label === "upper");
    if (lowerCached) {
      data.lower = {
        data: lowerCached.data,
        fileType: lowerCached.type as ModelFileTypes
      };
    }
    if (upperCached) {
      data.upper = {
        data: upperCached.data,
        fileType: upperCached.type as ModelFileTypes
      };
    }
  }

  return data;
};

const addCaseToZip = (caseName: string, parentZip: JSZip, data: ModelDownloadData): boolean => {
  // Create a folder for this case using the case name or a fallback
  const directoryName = caseName;
  const caseFolder = parentZip.folder(getUniqueFolderName(parentZip, directoryName));
  if (caseFolder != null) {
    // Add each blob to the case folder if available.
    if (data.lower) {
      caseFolder.file(`lowerModel.${data.lower.fileType}`, new Blob([data.lower.data]));
    }
    if (data.upper) {
      caseFolder.file(`upperModel.${data.upper.fileType}`, new Blob([data.upper.data]));
    }

    return true;
  }

  return false;
};

const generateAndDownloadZip = async (parentZip: JSZip, filename: string) => {
  // Generate the zip file as a blob and trigger the download.
  const zipBlob = await parentZip.generateAsync({ type: "blob" });
  const zipLink = document.createElement("a");
  zipLink.href = URL.createObjectURL(zipBlob);
  zipLink.download = filename;
  document.body.appendChild(zipLink);
  zipLink.click();
  document.body.removeChild(zipLink);
};

const DownloadRejectedDialog = ({
  numRejectedDesigns,
  download,
  openDialog,
  setOpenDialog
}: {
  numRejectedDesigns: number;
  download: () => void;
  openDialog: boolean;
  setOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { t } = useTranslation("cases");
  const {
    totalCost,
    isLoading: costIsLoading,
    error: costError
  } = useServiceCost({
    products: {
      [Service.ModelBasing]: numRejectedDesigns
    }
  });
  const { numCredits, isLoading: creditsIsLoading, error: creditsError } = useOrgCredits();
  const { [Scope.UpdateFinancials]: canBuyCredits, isLoading: permsIsLoading } = usePermissions();

  const isLoading =
    costIsLoading ||
    creditsIsLoading ||
    creditsError !== undefined ||
    costError !== undefined ||
    permsIsLoading ||
    numCredits === undefined;

  useEffect(() => {
    // Exit dialog on error.
    if (costError !== undefined || creditsError !== undefined) {
      setOpenDialog(false);
    }

    if (creditsError !== undefined) {
      toast.error(localizeError(t, ensureError(creditsError)));
    }
    if (costError !== undefined) {
      toast.error(localizeError(t, ensureError(costError)));
    }
  }, [creditsError, costError, t, setOpenDialog]);

  return (
    <DialogBox
      sx={{ ".MuiPaper-root": { width: "100%", maxWidth: "800px", margin: "16px" } }}
      args={undefined}
      title={isLoading || numCredits >= totalCost ? t("title.downloadRejectedDesigns") : t("common:title.insufficientCredits")}
      message={
        isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <Typography sx={{ whiteSpace: "pre-wrap" }}>
            {numCredits >= totalCost
              ? t("message.downloadRejectedDesigns", { count: numRejectedDesigns, cost: totalCost })
              : canBuyCredits
                ? t("message.downloadRejectedInsufficientCreditsAdmin", { count: numRejectedDesigns })
                : t("message.downloadRejectedInsufficientCredits", { count: numRejectedDesigns })}
          </Typography>
        )
      }
      onClose={(approve) => {
        if (numCredits === undefined) {
          return;
        }

        if (approve && numCredits >= totalCost) {
          download();
        } else if (approve && canBuyCredits) {
          window.open("/credits/", "_blank");
        }
      }}
      open={openDialog}
      setOpen={setOpenDialog}
      confirmDisable={isLoading}
      hideDecline={!isLoading && numCredits < totalCost && !canBuyCredits}
      declineText={isLoading || numCredits >= totalCost ? t("common:button.cancel") : t("common:button.close")}
      confirmText={
        isLoading || numCredits >= totalCost
          ? t("common:button.download")
          : canBuyCredits
            ? t("common:button.topUp")
            : t("common:button.close")
      }
    />
  );
};

export const DownloadDesignsButton = ({ selectedCase, dropdownPlacement = "bottom" }: DownloadDesignsButtonProps) => {
  const { t } = useTranslation("cases");
  const [open, setOpen] = useState<boolean>(false);
  const [openRejectedWarning, setOpenRejectedWarning] = useState<boolean>(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const [markCaseAsDownloaded] = useMarkCaseAsDownloadedApiV1CasesPutMutation();

  const downloadableIdsSelector = createSelector(
    [
      (state: RootState) => selectRtkData(state, CASE_TABLE_STATE_NAME, clientApi.endpoints.getCasesApiV1CasesGet.select)?.data?.content,
      (state: RootState) => selectCheckedIds(state, CASE_TABLE_STATE_NAME),
      (state: RootState, selectedCase: string[] | undefined) => selectedCase
    ],
    (content, checkedIds, selectedCase) => {
      if (selectedCase !== undefined) {
        checkedIds = selectedCase;
      }

      const dentalCases = checkedIds
        ? checkedIds
            .map((id) => content?.find((dentalCase) => dentalCase.id.toString() === id))
            .filter((dentalCase): dentalCase is CaseReadPublic => dentalCase !== undefined && dentalCase.id > 0)
        : [];

      const withDesignsIds = dentalCases
        .filter((dentalCase) => COMPLETE_CASE_STATUSES.includes(dentalCase.status))
        .map((dentalCase) => dentalCase.id.toString());

      const numRejectedDesigns = dentalCases.filter((dentalCase) => dentalCase.status === "rejected").length;

      const uploadedIds = dentalCases
        .filter((dentalCase) => !NOT_DOWNLOADABLE_CASE_STATUES.includes(dentalCase.status))
        .map((dentalCase) => dentalCase.id.toString());
      return { caseIdsWithDesigns: withDesignsIds, uploadedCaseIds: uploadedIds, numRejectedDesigns };
    },
    {
      memoizeOptions: {
        resultEqualityCheck: shallowEqual
      }
    }
  );

  // Usage:
  const { caseIdsWithDesigns, uploadedCaseIds, numRejectedDesigns } = useSelector((state: RootState) =>
    downloadableIdsSelector(state, selectedCase)
  );
  const numChecked = useSelector((state: RootState) => selectNumChecked(state, CASE_TABLE_STATE_NAME));

  const download = async (caseIds: string[], downloadType: "scan" | "based-model", markAsDownloaded: boolean) => {
    // Create parent zip and the cases folder
    const parentZip = new JSZip();

    const currentState = store.getState();
    const data = selectRtkData(currentState, CASE_TABLE_STATE_NAME, clientApi.endpoints.getCasesApiV1CasesGet.select)?.data?.content ?? [];
    const cases = caseIds.reduce(
      (accumulated, id) => {
        const dentalCase = data.find((caseItem) => caseItem.id.toString() === id.toString());
        if (dentalCase !== undefined) {
          accumulated[id.toString()] = dentalCase;
        }
        return accumulated;
      },
      {} as Record<string, CaseReadPublic>
    );

    if (!cases) {
      toast.error(t("toast.allCasesNotReadyToDownload"));
      return;
    }

    const downloadedCases: CaseReadPublic[] = [];
    let finishedCount = 0;
    const abortController = new AbortController();
    let aborted = false;
    abortController.signal.addEventListener("abort", () => (aborted = true));
    const onClick: MouseEventHandler<HTMLDivElement> = (e) => {
      e.stopPropagation();
      abortController.abort();
      toast.dismiss(loadingId);
    };
    const loadingId = showDownloadToast({ onClick, amount: finishedCount, total: caseIds.length });

    if (caseIds.length < numChecked) {
      toast.error(t("toast.someCasesNotReadyToDownload"));
    }

    await Promise.allSettled(
      Object.values(cases).map(async (dentalCase) => {
        let error;
        const caseId = dentalCase.id.toString();

        if (dentalCase.status === "rejected" && downloadType === "based-model") {
          // Mark case as downloaded on backend.
          // This will debit the cost of the case and allow it to be downloaded.
          try {
            await markCaseAsDownloaded({
              caseId: dentalCase.id
            }).unwrap();
          } catch (_) {
            return;
          }
        }

        try {
          const data = await getDataFromCache(caseId, downloadType);

          if (aborted) {
            return;
          }

          if (!data.lower || !data.upper) {
            // fetch presigned urls
            const responseData = await store
              .dispatch(
                clientApi.endpoints.downloadCaseApiV1CasesCaseIdUploadedGet.initiate({
                  caseId: Number(caseId)
                })
              )
              .unwrap();

            if (aborted) {
              return;
            }

            let lowerPresignedUrl: string | undefined;
            let upperPresignedUrl: string | undefined;
            if (downloadType === "scan") {
              lowerPresignedUrl = responseData.lower_scan_presigned_url;
              upperPresignedUrl = responseData.upper_scan_presigned_url;
            } else {
              lowerPresignedUrl = responseData.lower_model_presigned_url;
              upperPresignedUrl = responseData.upper_model_presigned_url;
            }

            if (lowerPresignedUrl !== undefined && data.lower === undefined) {
              data.lower = {
                data: await fetchModel(lowerPresignedUrl),
                fileType: getFileTypeFromS3URL(lowerPresignedUrl)
              };
            }
            if (upperPresignedUrl !== undefined && data.upper === undefined) {
              data.upper = {
                data: await fetchModel(upperPresignedUrl),
                fileType: getFileTypeFromS3URL(upperPresignedUrl)
              };
            }
          }

          if (data.lower !== undefined || data.upper !== undefined) {
            if (!addCaseToZip(dentalCase.name, parentZip, data)) {
              toast.error(t("toast.failedToDownloadCase", { caseName: dentalCase.name }));
            }
          } else {
            toast.error(t("toast.failedToDownloadCase", { caseName: dentalCase.name }));
          }
        } catch (err) {
          error = ensureError(err);
          toast.error(localizeError(t, error));
        }

        if (aborted) {
          return;
        }

        if (error === undefined) {
          downloadedCases.push(dentalCase);
        }
        finishedCount++;
        showDownloadToast({ id: loadingId, onClick, amount: finishedCount, total: caseIds.length });
      })
    );

    if (aborted) {
      return;
    }

    if (Object.keys(parentZip.files).length > 0) {
      let filename;
      if (downloadedCases.length == 1) {
        filename = downloadedCases[0].name;
      } else {
        filename =
          downloadType === "scan"
            ? t("files.cases", { time: new Date().toISOString() })
            : t("files.designs", { time: new Date().toISOString() });
      }

      try {
        await generateAndDownloadZip(parentZip, filename);
      } catch (_) {
        toast.error(t("toast.failedToGenerateZip"));
        return;
      }

      if (markAsDownloaded && downloadedCases.length !== 0) {
        await Promise.allSettled(
          downloadedCases.map(async (dentalCase) => {
            if (dentalCase.status !== "done") {
              return;
            }

            // Mark case as downloaded on backend
            try {
              const response = await markCaseAsDownloaded({
                caseId: dentalCase.id
              }).unwrap();
              if (response.status !== "downloaded") {
                // Status mismatch, notify the user. TODO: confirm toast messages with David and add to i18n
                toast.error(t("toast.statusUpdateFailed"));
              }
            } catch (_) {
              toast.error(t("toast.statusUpdateFailed"));
            }
          })
        );
      }
    }

    if (finishedCount != caseIds.length) {
      toast.error(
        t(downloadType === "scan" ? "toast.unableToDownloadCases" : "toast.unableToDownloadDesigns", {
          count: caseIds.length - finishedCount
        })
      );
    }

    toast.dismiss(loadingId);
  };

  const downloadDesigns = () => download(caseIdsWithDesigns, "based-model", true);
  const downloadDesignsWithRejectedCheck = () => {
    if (numRejectedDesigns !== 0) {
      setOpenRejectedWarning(true);
      return;
    }

    downloadDesigns();
  };
  const downloadCases = () => download(uploadedCaseIds, "scan", false);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  const disabled = uploadedCaseIds.length === 0 && caseIdsWithDesigns.length === 0;
  const hasUploadedCases = uploadedCaseIds.length !== 0;
  const hasCasesWithDesigns = caseIdsWithDesigns.length !== 0;

  return (
    <Fragment>
      <ButtonGroup variant="text" ref={anchorRef} aria-label="Download options" disabled={disabled}>
        <Button
          variant="contained"
          sx={{
            borderRadius: "100px 0 0 100px", // Apply border-radius only to the left side
            height: "32px",
            border: "6px",
            borderColor: "white",
            backgroundColor: theme.palette.primary.main
          }}
          onClick={hasCasesWithDesigns || disabled ? downloadDesignsWithRejectedCheck : downloadCases}
          startIcon={<DownloadDesignSvg color={theme.palette.primary.contrastText} />}
        >
          <Typography fontSize="inherit" fontWeight="inherit" noWrap>
            {hasCasesWithDesigns || disabled ? t("button.downloadDesigns") : t("label.downloadCases")}
          </Typography>
        </Button>
        <Divider
          color={disabled ? "#d7d7d7" : theme.palette.primary.contrastText}
          backgroundColor={disabled ? "#d7d7d7" : theme.palette.primary.main}
        />
        <Button
          sx={{
            borderRadius: "0 100px 100px 0", // Apply border-radius only to the right side
            height: "32px",
            backgroundColor: disabled ? "#d7d7d7" : theme.palette.primary.main
          }}
          size="small"
          aria-controls={open ? "split-button-menu" : undefined}
          aria-expanded={open ? "true" : undefined}
          aria-label={t("label.selectDownloadOption")}
          aria-haspopup="menu"
          onClick={handleToggle}
        >
          <ExpandedSvg color={theme.palette.primary.contrastText} />
        </Button>
      </ButtonGroup>

      <Popper
        sx={{ zIndex: 10 }}
        open={open}
        placement={dropdownPlacement}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === "bottom" ? "center top" : "center bottom"
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="split-button-menu" autoFocusItem>
                  <MenuItem disabled={!hasCasesWithDesigns} onClick={downloadDesignsWithRejectedCheck}>
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "8px" }}>
                      <DownloadDesignSvg width={"24px"} />
                      {t("button.downloadDesigns")}
                    </Box>
                  </MenuItem>
                  <MenuItem disabled={!hasUploadedCases} onClick={downloadCases}>
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "8px" }}>
                      <DownloadCaseSvg width={"24px"} />
                      {t("label.downloadCases")}
                    </Box>
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
      <DownloadRejectedDialog
        numRejectedDesigns={numRejectedDesigns}
        download={downloadDesigns}
        openDialog={openRejectedWarning}
        setOpenDialog={setOpenRejectedWarning}
      />
    </Fragment>
  );
};
